import { AnimatePresence, motion } from "framer-motion"
import { EllipsisVertical, Info, Loader2, Mic, Paperclip, Send, Square } from "lucide-react"
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react"
import { omit } from "remeda"
import { Button } from "../../atoms/Button"
import { useAudioRecording } from "../../hooks/useAudioRecording"
import { useAutosizeTextArea } from "../../hooks/useAutosizeTextarea"
import { CONSTANTS } from "../../lib/constants"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../molecules/DropdownMenu"
import { cn } from "../../utils"
import { AudioVisualizer } from "./AudioVisualizer"
import { FilePreview } from "./FilePreview"
import { InterruptPrompt } from "./InterruptPrompt"

export type NoteInputComponentProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  forwardedRef: React.Ref<HTMLTextAreaElement>
  dropdownPlacement?: "above" | "below"
}

export interface MessageInputMenuControl {
  key: string
  menuItem: React.ReactNode
  button: React.ReactNode
}

interface MessageInputBaseProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string
  submitOnEnter?: boolean
  stop?: () => void
  isGenerating: boolean
  enableInterrupt?: boolean
  transcribeAudio?: (blob: Blob) => Promise<string>
  onUploadFiles?: (files: File[]) => Promise<void> | void
  allowAttachments?: boolean
  acceptedFileTypes?: readonly string[]
  uploadFilesLabel?: string
  files?: File[] | null
  setFiles?: React.Dispatch<React.SetStateAction<File[] | null>>
  onSubmit?: (e?: React.FormEvent) => void
  interruptMessage: string
  isFakeCustomer?: boolean
  isClosed?: boolean
  isNoteMode?: boolean
  customControlsLeft?: React.ReactNode
  customControlsRight?: React.ReactNode
  customControlsMenu?: MessageInputMenuControl[]
  NoteInputComponent?: React.ComponentType<NoteInputComponentProps>
}

type MessageInputProps = MessageInputBaseProps

const acceptedImageTypes = CONSTANTS.acceptedImageTypes

export function MessageInput({
  acceptedFileTypes = acceptedImageTypes,
  uploadFilesLabel = "Upload images",
  placeholder,
  className,
  onKeyDown: onKeyDownProp,
  onPaste: onPasteProp,
  submitOnEnter = true,
  stop,
  isGenerating,
  enableInterrupt = true,
  transcribeAudio,
  onSubmit,
  interruptMessage,
  isFakeCustomer,
  isClosed,
  isNoteMode = false,
  customControlsLeft,
  customControlsRight,
  customControlsMenu,
  NoteInputComponent,
  ...props
}: MessageInputProps) {
  const acceptedFileTypesSet = useMemo(() => new Set<string>(acceptedFileTypes), [acceptedFileTypes])
  const [isUploadingFiles, setIsUploadingFiles] = useState(false)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const [showInterruptPrompt, setShowInterruptPrompt] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    isListening,
    isSpeechSupported,
    isRecording,
    isTranscribing,
    audioStream,
    toggleListening,
    stopRecording,
  } = useAudioRecording({
    transcribeAudio,
    onTranscriptionComplete: (text) => {
      props.onChange?.({ target: { value: text } } as unknown as ChangeEvent<HTMLTextAreaElement>)
    },
  })

  useEffect(() => {
    if (!isGenerating) {
      setShowInterruptPrompt(false)
    }
  }, [isGenerating])

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (submitOnEnter && event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()

      if (event.currentTarget.value.trim().length === 0 && !props.files?.length) {
        return
      }

      if (isGenerating && stop && enableInterrupt) {
        if (showInterruptPrompt) {
          stop()
          setShowInterruptPrompt(false)
          if (onSubmit) {
            onSubmit()
          } else {
            event.currentTarget.form?.requestSubmit()
          }
        } else if (props.value.trim().length > 0 || props.files?.length) {
          setShowInterruptPrompt(true)
          return
        }
      } else {
        if (onSubmit) {
          onSubmit()
        } else {
          event.currentTarget.form?.requestSubmit()
        }
      }
    }

    onKeyDownProp?.(event)
  }

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [textAreaHeight, setTextAreaHeight] = useState<number>(0)

  useEffect(() => {
    if (textAreaRef.current) {
      setTextAreaHeight(textAreaRef.current.offsetHeight)
    }
  }, [props.value, placeholder, isNoteMode])

  const hasFiles = !!props.files?.length
  const showFileList = hasFiles

  useAutosizeTextArea({
    ref: textAreaRef,
    maxHeight: 100,
    borderWidth: 1,
    dependencies: [props.value, placeholder, showFileList, isNoteMode],
  })

  const messageIsEmpty = props.value.trim().length === 0

  const isSendDisabled =
    (messageIsEmpty && !hasFiles) || isGenerating || props.disabled || isUploadingFiles

  const shouldShowUploadControl = !!props.onUploadFiles && !isNoteMode
  const canUploadFiles = !!props.allowAttachments && !!props.onUploadFiles && !!props.setFiles
  const canInteractWithUpload = canUploadFiles && !props.disabled && !isUploadingFiles

  const uploadFiles = async (selectedFiles: File[]) => {
    const files = selectedFiles.filter((file) => acceptedFileTypesSet.has(file.type))

    if (!files.length || !canInteractWithUpload) {
      return
    }

    props.setFiles?.((currentFiles) => [...(currentFiles ?? []), ...files])
    setIsUploadingFiles(true)

    try {
      await props.onUploadFiles?.(files)
    } catch (error) {
      props.setFiles?.((currentFiles) => {
        const filtered = (currentFiles ?? []).filter((file) => !files.includes(file))
        return filtered.length ? filtered : null
      })
      throw error
    } finally {
      setIsUploadingFiles(false)
    }
  }

  const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await uploadFiles(Array.from(event.target.files ?? []))
    event.target.value = ""
  }

  const onPaste = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    onPasteProp?.(event)

    if (!canInteractWithUpload || event.defaultPrevented) {
      return
    }

    const clipboardFiles = Array.from(event.clipboardData.files)
    const filesFromItems = Array.from(event.clipboardData.items).flatMap((item) => {
      const file = item.getAsFile()
      return file ? [file] : []
    })
    const files = (clipboardFiles.length ? clipboardFiles : filesFromItems).filter((file) =>
      acceptedFileTypesSet.has(file.type),
    )

    if (!files.length) {
      return
    }

    event.preventDefault()
    await uploadFiles(files)
  }

  const uploadFileInput = shouldShowUploadControl && (
    <input
      ref={fileInputRef}
      type="file"
      accept={acceptedFileTypes.join(",")}
      multiple
      className="hidden"
      onChange={onFileInputChange}
      disabled={!canInteractWithUpload}
    />
  )

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canInteractWithUpload) return

    event.preventDefault()
    event.stopPropagation()
    setIsDraggingFiles(true)
  }

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canInteractWithUpload) return

    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "copy"
    setIsDraggingFiles(true)
  }

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canInteractWithUpload) return

    event.preventDefault()
    event.stopPropagation()

    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) return

    setIsDraggingFiles(false)
  }

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!canInteractWithUpload) return

    event.preventDefault()
    event.stopPropagation()
    setIsDraggingFiles(false)
    await uploadFiles(Array.from(event.dataTransfer.files))
  }

  const submitControl =
    !isNoteMode &&
    (isGenerating && stop ? (
      <Button
        type="button"
        size="icon"
        aria-label="Stop generating"
        onClick={stop}
        disabled={props.disabled || isUploadingFiles}
      >
        <Square className="h-3 w-3 animate-pulse" fill="currentColor" />
      </Button>
    ) : (
      <Button
        type="submit"
        size="icon"
        variant="default"
        className="transition-opacity"
        aria-label="Send message"
        disabled={isSendDisabled}
      >
        <Send />
      </Button>
    ))

  const inlineSendControls = (
    <>
      {shouldShowUploadControl && (
        <Button
          type="button"
          size="icon"
          variant="outline"
          aria-label={uploadFilesLabel}
          onClick={() => fileInputRef.current?.click()}
          disabled={!canInteractWithUpload}
        >
          {isUploadingFiles ? <Loader2 className="animate-spin" /> : <Paperclip />}
        </Button>
      )}
      {isSpeechSupported && (
        <Button
          type="button"
          variant="outline"
          className={cn(isListening && "text-primary")}
          aria-label="Voice input"
          size="icon"
          onClick={toggleListening}
          disabled={props.disabled || isUploadingFiles}
        >
          <Mic />
        </Button>
      )}
      {submitControl}
    </>
  )

  const menuControls: MessageInputMenuControl[] = [
    ...(customControlsMenu ?? []),
    ...(shouldShowUploadControl
      ? [
          {
            key: "upload-files",
            menuItem: (
              <DropdownMenuItem
                disabled={!canInteractWithUpload}
                onSelect={() => fileInputRef.current?.click()}
              >
                {isUploadingFiles ? <Loader2 className="animate-spin" /> : <Paperclip />}
                <span>{uploadFilesLabel}</span>
              </DropdownMenuItem>
            ),
            button: (
              <Button
                type="button"
                size="icon"
                variant="secondary"
                aria-label={uploadFilesLabel}
                title={uploadFilesLabel}
                onClick={() => fileInputRef.current?.click()}
                disabled={!canInteractWithUpload}
              >
                {isUploadingFiles ? <Loader2 className="animate-spin" /> : <Paperclip />}
                <span className="sr-only">{uploadFilesLabel}</span>
              </Button>
            ),
          },
        ]
      : []),
    ...(isSpeechSupported
      ? [
          {
            key: "voice-input",
            menuItem: (
              <DropdownMenuItem
                disabled={props.disabled || isUploadingFiles}
                onSelect={toggleListening}
              >
                <Mic />
                <span>{isListening ? "Stop voice input" : "Voice input"}</span>
              </DropdownMenuItem>
            ),
            button: (
              <Button
                type="button"
                size="icon"
                variant="secondary"
                aria-label={isListening ? "Stop voice input" : "Voice input"}
                title={isListening ? "Stop voice input" : "Voice input"}
                className={cn(isListening && "text-primary")}
                onClick={toggleListening}
                disabled={props.disabled || isUploadingFiles}
              >
                <Mic />
                <span className="sr-only">{isListening ? "Stop voice input" : "Voice input"}</span>
              </Button>
            ),
          },
        ]
      : []),
  ]

  const dropdownControls = (() => {
    if (menuControls.length === 0) return null
    if (menuControls.length === 1) return menuControls[0]?.button

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="icon" variant="secondary" aria-label="More actions">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {menuControls.map((control) => (
            <React.Fragment key={control.key}>{control.menuItem}</React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  })()

  const showCustomControls = customControlsLeft || customControlsRight || menuControls.length > 0
  const showControlsRow = showCustomControls || isFakeCustomer

  return (
    <div
      className={cn(
        "flex flex-col w-full items-center rounded-xl overflow-hidden border border-input bg-background text-sm disabled:cursor-not-allowed transition-colors",
        isDraggingFiles && "border-primary bg-primary/5 ring-2 ring-primary/20",
        isNoteMode && "border-warning/20",
      )}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {uploadFileInput}

      {enableInterrupt && (
        <InterruptPrompt
          isOpen={showInterruptPrompt}
          close={() => setShowInterruptPrompt(false)}
          interruptMessage={interruptMessage}
        />
      )}

      <RecordingPrompt isVisible={isRecording} onStopRecording={stopRecording} />

      {showFileList && (
        <div className="w-full overflow-x-auto overflow-y-visible pl-3 pr-5 pt-3">
          <div className="flex gap-3">
            <AnimatePresence mode="popLayout">
              {props.files?.map((file) => {
                return (
                  <FilePreview
                    key={file.name + String(file.lastModified)}
                    file={file}
                    onRemove={() => {
                      props.setFiles?.((files) => {
                        if (!files) return null

                        const filtered = Array.from(files).filter((f) => f !== file)
                        if (filtered.length === 0) return null
                        return filtered
                      })
                    }}
                  />
                )
              })}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="flex w-full relative">
        {isNoteMode && NoteInputComponent ? (
          <NoteInputComponent
            aria-label="Write your message here"
            placeholder={placeholder}
            forwardedRef={textAreaRef}
            dropdownPlacement="above"
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            className={cn(
              "grow p-3 resize-none w-full placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus:border-transparent",
              className,
              isClosed && "placeholder:italic",
              "bg-warning/10 italic",
            )}
            disabled={props.disabled || isUploadingFiles}
            {...omit(props, ["allowAttachments", "files", "setFiles", "onUploadFiles"])}
            rows={1}
          />
        ) : (
          <textarea
            aria-label="Write your message here"
            placeholder={placeholder}
            ref={textAreaRef}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            className={cn(
              "grow p-4 resize-none w-full placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus:border-transparent",
              className,
              isClosed && "placeholder:italic",
              isNoteMode && "bg-warning/10 italic",
            )}
            disabled={props.disabled || isUploadingFiles}
            {...omit(props, ["allowAttachments", "files", "setFiles", "onUploadFiles"])}
            rows={1}
          />
        )}
        {!showControlsRow && (
          <div className="flex flex-row w-fit py-2.5 px-3 gap-2 items-end self-end">
            {inlineSendControls}
          </div>
        )}
      </div>

      {showControlsRow && (
        <div
          className={cn(
            "flex flex-row flex-wrap items-start gap-2 pb-2.5 px-3 z-20 w-full",
            customControlsLeft ? "justify-between" : "justify-end",
            isNoteMode && "bg-warning/20",
          )}
        >
          {customControlsLeft}
          <div className="flex flex-row gap-2 justify-end">
            {customControlsRight}
            {dropdownControls}
            {submitControl}
          </div>
        </div>
      )}

      {!props.disabled && (
        <RecordingControls
          isRecording={isRecording}
          isTranscribing={isTranscribing}
          audioStream={audioStream}
          textAreaHeight={textAreaHeight}
          onStopRecording={stopRecording}
        />
      )}
    </div>
  )
}
MessageInput.displayName = "MessageInput"

function TranscribingOverlay() {
  return (
    <motion.div
      className="flex h-full w-full flex-col items-center justify-center rounded-xl bg-background/80 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="relative">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <motion.div
          className="absolute inset-0 h-8 w-8 animate-pulse rounded-full bg-primary/20"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1.2, opacity: 1 }}
          transition={{
            duration: 1,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
        />
      </div>
      <p className="mt-4 text-sm font-medium text-muted-foreground">Transcribing audio...</p>
    </motion.div>
  )
}

interface RecordingPromptProps {
  isVisible: boolean
  onStopRecording: () => void
}

function RecordingPrompt({ isVisible, onStopRecording }: RecordingPromptProps) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ top: 0, filter: "blur(5px)" }}
          animate={{
            top: -40,
            filter: "blur(0px)",
            transition: {
              type: "spring",
              filter: { type: "tween" },
            },
          }}
          exit={{ top: 0, filter: "blur(5px)" }}
          className="absolute left-1/2 flex -translate-x-1/2 cursor-pointer overflow-hidden whitespace-nowrap rounded-full border bg-background py-1 text-center text-sm text-muted-foreground"
          onClick={onStopRecording}
        >
          <span className="mx-2.5 flex items-center">
            <Info className="mr-2 h-3 w-3" />
            Click to finish recording
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface RecordingControlsProps {
  isRecording: boolean
  isTranscribing: boolean
  audioStream: MediaStream | null
  textAreaHeight: number
  onStopRecording: () => void
}

function RecordingControls({
  isRecording,
  isTranscribing,
  audioStream,
  textAreaHeight,
  onStopRecording,
}: RecordingControlsProps) {
  if (isRecording) {
    return (
      <div
        className="absolute inset-[1px] z-50 overflow-hidden rounded-xl"
        style={{ height: textAreaHeight - 2 }}
      >
        <AudioVisualizer stream={audioStream} isRecording={isRecording} onClick={onStopRecording} />
      </div>
    )
  }

  if (isTranscribing) {
    return (
      <div
        className="absolute inset-[1px] z-50 overflow-hidden rounded-xl"
        style={{ height: textAreaHeight - 2 }}
      >
        <TranscribingOverlay />
      </div>
    )
  }

  return null
}
